/* eslint-disable max-lines */

import { ThemeName } from 'constants/theme';
import { Description, Fee, MarketingFeeDetails, OffersListing } from 'models';

import { UitkIconSize } from 'uitk-react-icons';

import { CrossSellProductType, LodgingEnrichedMessage, PropertyInfoOffersBexFeatures, Theme } from '__generated__/api';

import LodgingEnrichedMessageModel = LodgingEnrichedMessage.Fragment;
import PropertyHighlightSection = PropertyInfoOffersBexFeatures.PropertyHighlightSection;

/**
 * Used for rendering uitk badges
 */
export interface BadgeModel {
  text?: string;
  icon?: string;
  iconTitle?: string;
  theme?: Theme;
  mark?: string;
}

/**
 * Used for rendering search result badges
 */
export interface PropertyListingBadgeModel {
  primary?: BadgeModel;
  secondary?: BadgeModel;
  tertiary?: BadgeModel;
  limitedAvailability?: string;
}

/**
 * Used for rendering Legal Disclaimer
 */

export interface LegalDisclaimer {
  content: LegalDisclaimerContent[];
}

export interface LegalDisclaimerContent {
  markupType: string;
  text: string;
}

/**
 * Used for rendering rate plan badges
 */
export interface RatePlanBadgeModel {
  badge?: BadgeModel;
  limitedAvailability?: string;
}

export interface BadgeDetailsModel {
  displayPrice: number;
  minRoomsLeft: number;
  isSponsored: boolean;
}

export interface DynamicRateRuleViewModel {
  description: string;
  expirationSeconds?: number;
  isSameDay: boolean;
  discountPercent?: number;
}

/**
 * used for rendering HotelSearchMessagingResult on Search page.
 */
export interface HotelSearchMessagingResultTitle {
  text?: string;
  icon?: string;
  theme?: string;
}

/**
 * Used for rendering loyalty messaging on HSR
 */
export interface LoyaltyMessagingModel {
  saveWithPointsMessage?: string;
  saveWithPointsActionMessage?: string;
}

/**
 * Inner Class for ShortList Model.
 */
export interface MetaDataModel {
  hotelId: string;
  chkIn: string;
  chkOut: string;
  roomConfiguration: string;
}

/**
 * View model to display the Messaging Card component.
 */

export interface MessagingCardActionViewModel {
  href?: string;
  text?: string;
  rfrr?: string;
  actionDetails?: {
    name: string;
    details?: string;
    searchOption?: {
      name?: string;
      value?: string;
    };
    title?: string;
    accessibilityLabel?: string;
  };
  linkAction?: string;
}
export interface MessagingCardViewModel extends OffersListing {
  titleModel?: MessageViewModel;
  subtitleModel?: MessageViewModel;
  actionModel?: {
    primary?: MessagingCardActionViewModel;
    secondary?: MessagingCardActionViewModel;
  };
  type?: string;
  messageType?: string;
}

/**
 * View model to display a message in the Messaging Card component.
 */
export interface MessageViewModel {
  text?: string;
  icon?: string;
  illustration?: string;
  theme?: string;
  mark?: string;
}

export interface OmnitureProperties {
  pageName?: string;
  channel?: string;
  products?: string;
  events?: string;
  eVar2?: string;
  eVar5?: string; // advance purchase window
  eVar6?: string; // length of stay
  eVar9?: string; // drr message
  prop2?: string;
  prop5?: string; // check in
  prop6?: string; // check out
  prop9?: string; // price diff
  prop36?: string; // error details
  prop46?: string; // bookable TSPIDs, 21:0|69:3|81:5
  prop47?: string;
}

export interface ParagraphViewModel {
  lines?: string[];
  heading?: string;
}

export interface ParagraphTitleViewModel {
  title?: string;
  content?: string[];
}

export interface PriceViewModel {
  lead?: string;
  strikeOut?: string;
  leadAmount?: number;
  strikeOutType?: any; // enum StrikeoutType - NOT YET IMPLEMENTED
  label?: string;
  taxAndFees?: string;
  feeDetails?: Fee[];
  priceMessage?: Description;
  numberOfNightsAndRooms?: string;
  qualifier?: string;
  strikeoutDisclaimer?: string;
  pointsApplied?: string;
  marketingFeeDetails?: MarketingFeeDetails;
  priceMessages?: Message[];
  priceMessaging?: LodgingEnrichedMessageModel[];
  options?: Options[];
  // a11yPrice should be mandatory after its added to bex api
  a11yPrice?: string;
  a11yStrikeThroughDialogTrigger?: string;
  feeMessages?: LodgingEnrichedMessageModel[];
}

export interface Message {
  value: string;
}

export interface Options {
  leadingCaption?: string;
  displayPrice?: DisplayPrice;
  strikeOut?: DisplayPrice;
  loyaltyPrice?: LoyaltyPrice;
  disclaimer?: Disclaimer;
}

export interface Disclaimer {
  value: string;
}

export interface DisplayPrice {
  formatted: string;
}

export interface LoyaltyPrice {
  amount?: LoyaltyAmount;
  totalStrikeOutPoints?: LoyaltyAmount;
  unit?: string;
}

export interface LoyaltyAmount {
  formatted: string;
}

/**
 * used for rendering PrimaryCardMessagingModel in Search Results card.
 */
export interface PrimaryCardMessagingModel {
  type?: any; // enum PrimaryMessagingType - NOT YET IMPLEMENTED
  description?: string;
  theme?: ThemeName;
}

/**
 * Criteria dervied from Search and Hotel Detail Criteria.
 */
export interface ShortlistCriteria {
  startDate?: string;
  endDate?: string;
  adults?: string;
  children?: string;
}

/**
 * ShortListModel for Universal Curator Response for Shortlisted Hotel.
 */
export interface ShortlistModel {
  favorited: boolean;
  saveUrl: string;
  removeUrl: string;
  meta: MetaDataModel;
}

export interface EtpViewModel {
  PAY_NOW: ParagraphViewModel[];
  PAY_LATER?: ParagraphViewModel[];
  PAY_LATER_WITH_DEPOSIT?: ParagraphViewModel[];
}

/**
 * FlyInModel for authentication success messages.
 */
export interface FlyInModel {
  text: string;
  icon: string;
}

/**
 * RewardsDialogContentModel for RewardsEducationDialog.
 */
export interface RewardsDialogContentModel {
  header: string;
  body: {
    type: string;
    content: string | string[];
    icon?: string;
  }[];
  button: {
    text: string;
    rfrr: string;
    href: string;
  };
}

/**
 * RewardsDialogModel for RewardsEducationDialog.
 */
export interface RewardsDialogModel {
  rewardsEducation: RewardsDialogContentModel;
}

/**
 * Used for rendering icons
 */
export interface Icon {
  id: string;
  description?: string;
  name?: string;
  size?: UitkIconSize;
  title?: string;
}

/**
 * FeedbackModel for Universal Curator Response for Feedback
 */
export interface FeedbackModel {
  feedbackText: string;
}

/**
 * Model to display exit intent information
 */
export interface ExitIntentModel {
  logoUrl?: string;
  clickHereLinkUrl?: string;
  backgroundImageUrl?: string;
  message: ExitIntentMessage;
}

/**
 * Exit intent messaging model
 */
export interface ExitIntentMessage {
  headingOne?: string;
  headingTwo?: string;
  headingThree?: string;
  couponCode?: string;
  percentOff?: string;
  termsAndConditionsOne?: string;
  termsAndConditionsTwo?: string;
  clickHere?: string;
}

export interface PriceBreakDownFeesViewModel {
  id: string;
  feeTypeDescription?: string;
  detail?: string;
  fee?: string;
}
export interface PriceLockupStids {
  lockupId?: string;
  strikeThroughId?: string;
  subtextFirstId?: string;
  subtextStandardId?: string;
}

export interface PriceBreakDownDetailsViewModel {
  header: string;
  priceBreakDownEntries?: PriceBreakDownEntriesViewModel[];
  footer?: PriceBreakDownEntriesViewModel;
  unbundledEntries?: PriceBreakDownEntriesViewModel[];
  ctaLabel?: string;
}

export interface PriceBreakDownEntriesViewModel {
  amount?: string;
  description?: string;
  detail?: string;
}

export interface PriceSummary {
  priceSummaryHeading?: LodgingEnrichedMessageModel;
  sections: PriceSummarySection[];
  footer?: PriceSummaryFooter;
  disclaimers?: LodgingEnrichedMessageModel[];
}

export interface PriceSummarySection {
  sectionHeading?: LodgingEnrichedMessageModel;
  items?: PriceSummaryLineItem[];
  sectionFooter?: PriceSummarySectionFooter;
}

export interface PriceSummaryFooter {
  footerHeading?: LodgingEnrichedMessageModel;
  footerMessages?: LodgingEnrichedMessageModel[];
}

export interface PriceSummaryLineItem {
  name: PriceSummaryLineItemEntry;
  value: PriceSummaryLineItemEntry;
}

export interface PriceSummaryLineItemEntry {
  primaryMessage: LodgingEnrichedMessageModel;
  secondaryMessages?: LodgingEnrichedMessageModel[];
}

export interface PriceSummarySectionFooter {
  footerHeading?: LodgingEnrichedMessageModel;
  footerMessages?: LodgingEnrichedMessageModel[];
}

export type FallbackListingViewModel = MessagingCardViewModel;

export interface StaticLodgingMultiItemCrossSellViewModel {
  crossSellProductType: CrossSellProductType | undefined;
  fallbackListingViewModel: FallbackListingViewModel | undefined;
}

export type UitkHeadingLevel = 1 | 2 | 3 | 4 | 5 | 6;
export type UitkHeadingSize = 300 | 400 | 500 | 600 | 700 | 800 | 900;

const uitkHeadingLevels = [1, 2, 3, 4, 5, 6];
const uitkHeadingSizes = [300, 400, 500, 600, 700, 800, 900];

export const isUitkHeaderLevelValid = (level: number): level is UitkHeadingLevel => uitkHeadingLevels.includes(level);
export const isUitkHeaderSizeValid = (size: number): size is UitkHeadingSize => uitkHeadingSizes.includes(size);

export interface LodgingHeadingViewModel {
  value: string;
  size: UitkHeadingSize;
  level: UitkHeadingLevel;
}

export type PropertyHighlightSectionModel = PropertyHighlightSection;
